/**
 * @author Michael Hasenstein <hasenstein@yahoo.com>
 * @copyright REFINIO GmbH 2021
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
import { wait } from '../../util/promise.js';
import { stringify } from '../../util/sorted-stringify.js';
// IndexedDB setup for Web Workers
const DB_NAME = 'ONE_SettingsStore';
const DB_VERSION = 1;
const STORE_NAME = 'settings';
let db = null;
// Detect if we're in a Web Worker (no localStorage available)
// Must be a function to avoid checking before platform is loaded
function isWebWorker() {
    return typeof localStorage === 'undefined';
}
// Initialize IndexedDB for Web Workers
async function initDB() {
    if (db)
        return db;
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onerror = () => {
            console.error('[SettingsStore] Failed to open IndexedDB:', request.error);
            reject(request.error);
        };
        request.onsuccess = () => {
            db = request.result;
            resolve(db);
        };
        request.onupgradeneeded = (event) => {
            const database = event.target.result;
            if (!database.objectStoreNames.contains(STORE_NAME)) {
                database.createObjectStore(STORE_NAME);
            }
        };
    });
}
/**
 * Settings storage for {@link PLATFORMS.BROWSER}
 * Uses localStorage in main thread, IndexedDB in Web Workers
 * @internal
 */
export const SettingsStore = {
    // This is async because the node.js API is asynchronous
    getItem: async (key) => {
        if (isWebWorker()) {
            // Web Worker: use IndexedDB
            const database = await initDB();
            return new Promise((resolve, reject) => {
                const transaction = database.transaction([STORE_NAME], 'readonly');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.get(key);
                request.onsuccess = () => {
                    const value = request.result;
                    resolve(value === undefined || value === null ? undefined : value);
                };
                request.onerror = () => {
                    console.error('[SettingsStore] Failed to get item:', request.error);
                    reject(request.error);
                };
            });
        }
        else {
            // Main thread: use localStorage
            const item = localStorage.getItem(key);
            if (item === null) {
                return undefined;
            }
            return JSON.parse(item);
        }
    },
    // This is async because the node.js API is asynchronous. Also, localStorage gives no
    // guarantee about the value having been written to disk, so we give it a minimum amount
    // of time to be somewhat more sure (of course we cannot check from Javascript).
    setItem: async (key, value) => {
        if (isWebWorker()) {
            // Web Worker: use IndexedDB
            const database = await initDB();
            return new Promise((resolve, reject) => {
                const transaction = database.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.put(value, key);
                request.onsuccess = () => {
                    resolve();
                };
                request.onerror = () => {
                    console.error('[SettingsStore] Failed to set item:', request.error);
                    reject(request.error);
                };
            });
        }
        else {
            // Main thread: use localStorage
            localStorage.setItem(key, stringify(value));
            await wait(1);
        }
    },
    // This is async because the node.js API is asynchronous. Also, localStorage gives no
    // guarantee about the value having been written to disk, so we give it a minimum amount
    // of time to be somewhat more sure (of course we cannot check from Javascript).
    removeItem: async (key) => {
        if (isWebWorker()) {
            // Web Worker: use IndexedDB
            const database = await initDB();
            return new Promise((resolve, reject) => {
                const transaction = database.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.delete(key);
                request.onsuccess = () => {
                    resolve();
                };
                request.onerror = () => {
                    console.error('[SettingsStore] Failed to remove item:', request.error);
                    reject(request.error);
                };
            });
        }
        else {
            // Main thread: use localStorage
            localStorage.removeItem(key);
            await wait(1);
        }
    },
    // This is async because the node.js API is asynchronous. Also, localStorage gives no
    // guarantee about the value having been written to disk, so we give it a minimum amount
    // of time to be somewhat more sure (of course we cannot check from Javascript).
    clear: async () => {
        if (isWebWorker()) {
            // Web Worker: use IndexedDB
            const database = await initDB();
            return new Promise((resolve, reject) => {
                const transaction = database.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.clear();
                request.onsuccess = () => {
                    resolve();
                };
                request.onerror = () => {
                    console.error('[SettingsStore] Failed to clear store:', request.error);
                    reject(request.error);
                };
            });
        }
        else {
            // Main thread: use localStorage
            localStorage.clear();
            await wait(1);
        }
    }
};
//# sourceMappingURL=settings-store.js.map